/**
 * ApplicabilityChip — renders the Universal Applicability Engine's
 * resolution on a rule card.
 *
 * Surface shape:
 *   • RESOLVED → small "entity · WALL+DOOR" pill (matched on accent)
 *   • HITL_REVIEW → yellow "Needs review · <reason>" pill
 *
 * Input ``applicability`` is the per-rule ``_applicability`` blob produced
 * by ``services/applicability_inference.py`` and persisted on
 * ``bep_registry.manifest['applicability_index'][rule_id]``. Shape::
 *
 *   {
 *     applicability_type: 'entity' | 'discipline' | 'classification' |
 *                         'property_set' | 'property' | 'material' |
 *                         'quantity' | 'file' | 'model' | 'project' |
 *                         'custom',
 *     validation_target:  'property' | 'classification' | …,
 *     ifc_entities:       string[],
 *     exclude_ifc_entities: string[],
 *     discipline:         string,
 *     property_scope:     string,
 *     confidence:         number,
 *     status:             'RESOLVED' | 'HITL_REVIEW',
 *     reason:             string,
 *     signals:            string[],
 *   }
 *
 * When ``applicability`` is missing or empty the chip renders nothing so
 * older rules (extracted before the engine existed and never
 * backfilled) don't break the UI.
 */
import { Badge } from './ui/badge';
import { AlertCircle, ShieldCheck } from 'lucide-react';

const TYPE_LABEL = {
  entity:        'entity',
  discipline:    'discipline',
  classification: 'class',
  property_set:  'pset',
  property:      'property',
  material:      'material',
  quantity:      'qty',
  file:          'file',
  model:         'model',
  project:       'project',
  custom:        'custom',
};

export default function ApplicabilityChip({ applicability, compact = false }) {
  if (!applicability || !applicability.applicability_type) return null;
  const a = applicability;
  const isHitl = a.status === 'HITL_REVIEW';
  const typeLabel = TYPE_LABEL[a.applicability_type] || a.applicability_type;
  const entities = Array.isArray(a.ifc_entities) ? a.ifc_entities : [];
  const entitiesLabel = entities.length === 0
    ? ''
    : entities.length <= 3
      ? entities.join(' + ')
      : `${entities.slice(0, 2).join(' + ')} +${entities.length - 2}`;
  const hasConfidence = typeof a.confidence === 'number';
  const confidencePct = hasConfidence ? Math.round(a.confidence * 100) : null;
  const confidenceLabel = hasConfidence ? `${confidencePct}%` : '—';

  if (isHitl) {
    return (
      <Badge
        className="text-[10px] px-1.5 bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/40 inline-flex items-center gap-1"
        data-testid="applicability-chip-hitl"
        title={
          (a.reason || 'Applicability could not be confidently inferred.') +
          (a.signals && a.signals.length ? `\n\nSignals: ${a.signals.join(', ')}` : '')
        }
      >
        <AlertCircle className="h-3 w-3" />
        Needs review {compact ? '' : `· ${typeLabel} · ${confidenceLabel}`}
      </Badge>
    );
  }

  return (
    <Badge
      className="text-[10px] px-1.5 bg-accent/10 text-accent border border-accent/30 inline-flex items-center gap-1"
      data-testid="applicability-chip-resolved"
      title={
        `Applicability: ${typeLabel}` +
        (a.discipline ? ` · ${a.discipline}` : '') +
        (entities.length ? `\nIFC entities: ${entities.join(', ')}` : '') +
        (a.property_scope ? `\nProperty scope: ${a.property_scope}` : '') +
        `\nConfidence: ${confidenceLabel}` +
        (a.reason ? `\n${a.reason}` : '')
      }
    >
      <ShieldCheck className="h-3 w-3" />
      {typeLabel}{entitiesLabel ? ` · ${entitiesLabel}` : ''}
      {!compact && a.applicability_type === 'discipline' && a.discipline ? ` · ${a.discipline}` : ''}
    </Badge>
  );
}
